import { ReactNode } from "react"
import { Container } from "reactstrap"
import LoadingState from "Components/organisms/loading-state"
import DetailPageHeader from "Components/molecule/cards/detail-page-header"
import ActionButton from "Components/atoms/buttons/action-button"
import Chip from "Components/atoms/chip"
import Employee from 'models/employee';
import Breadcrumb from "Components/organisms/breadcrumb"
import { HelpIcon, PencilIcon, TrashIcon } from "Components/atoms/icons"
import { EMPLOYEE_STATUS_VARIANT } from "models/employment-status"
import { EMPLOYEE_TYPE_VARIANT } from "models/employee-type"
import { formatDate } from "utils/tools"
import { stripDepartmentPrefix } from "utils/department-options"

interface EmployeeDetailsTemplateProps {
    employee: Employee | null
    isLoadingEmployee: boolean
    isEditing: boolean,
    willingToRelocate: boolean,
    esaExempt: boolean,
    contractEndDate: string,
    handleBack: () => any,
    handleOpenHelpDocument?: () => any,
    // The only Edit button of an employee / IC profile
    onEdit: () => void,
    onSave: () => void,
    onCancel: () => void,
    // Consultants only (needs the Consultants screen)
    onDelete?: () => void,
    onChangeWillingToRelocate: (value: boolean) => void,
    onChangeEsaExempt: (value: boolean) => void,
    onChangeContractEndDate: (value: string) => void,
    onClickEmployee: (employeeId: string) => void,
    modal?: ReactNode
}

const fullName = (e?: { firstName?: string, lastName?: string, commonName?: string | null } | null) =>
    e ? `${e.commonName ?? e.firstName ?? ''} ${e.lastName ?? ''}`.trim() : '-'

const yesNo = (value?: boolean | null) => value ? 'Yes' : 'No'

const Field = ({ label, children }: { label: string, children: ReactNode }) => (
    <div className="employee-details__field">
        <div className="employee-details__label">{label}</div>
        <div className="employee-details__value">{children ?? '-'}</div>
    </div>
)

const Section = ({ title, children }: { title: string, children: ReactNode }) => (
    <div className="section-container">
        <h5 className="mb-3">{title}</h5>
        {children}
    </div>
)

// Everything IbexIQ stores about an employee: profile, contact, employment, location,
// demographics, designations, emergency contacts, vacation and direct reports.
const EmployeeDetailsTemplate = ({
    employee: e,
    isLoadingEmployee,
    isEditing,
    willingToRelocate,
    esaExempt,
    contractEndDate,
    onEdit,
    onSave,
    onCancel,
    onDelete,
    onChangeWillingToRelocate,
    onChangeEsaExempt,
    onChangeContractEndDate,
    onClickEmployee,
    handleBack,
    handleOpenHelpDocument,
    modal,
}: EmployeeDetailsTemplateProps) => {
    const address = e?.location?.address
    const status = e?.employeeStatus?.description
    const type = e?.employeeType?.name

    return <div className="page-content">
        <Container fluid style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Breadcrumb
                title={"Human Resources"}
                breadcrumbItem={"Employee Details"}
                actions={<>
                    <ActionButton onClick={handleBack}>Back</ActionButton>
                    {
                        !isEditing ? (
                            <ActionButton onClick={onEdit}>Edit <PencilIcon /></ActionButton>
                        ) : (
                            <>
                                <ActionButton onClick={onSave}>Save</ActionButton>
                                <ActionButton onClick={onCancel}>Cancel</ActionButton>
                            </>
                        )
                    }
                    {onDelete && !isEditing && (
                        <ActionButton className="bg-danger" onClick={onDelete}>Delete <TrashIcon className="text-white" /></ActionButton>
                    )}
                    {handleOpenHelpDocument && (
                        <ActionButton onClick={() => handleOpenHelpDocument()}>
                            Help <HelpIcon />
                        </ActionButton>
                    )}
                </>}
            />
            {
                isLoadingEmployee || !e ?
                    <div className="section-container">
                        <LoadingState
                            title="Loading Employee"
                            subtitle="Fetching employee details..."
                        />
                    </div>
                    :
                    <>
                        <DetailPageHeader
                            fullWidth
                            title={`${fullName(e)}${e.employeeCode ? ` (${e.employeeCode})` : ''}`}
                            subtitle={[e.jobTitle?.description, e.department ? stripDepartmentPrefix(e.department.description) : null].filter(Boolean).join(' · ')}
                            rows={[
                                { label: "Status", value: status ? <Chip variant={EMPLOYEE_STATUS_VARIANT[status]}>{status}</Chip> : '-' },
                                { label: "Type", value: type ? <Chip variant={EMPLOYEE_TYPE_VARIANT[type]}>{type}</Chip> : '-' },
                            ]}
                        />

                        <div className="employee-details__grid">
                            <Section title="Contact">
                                <Field label="Company Email">{e.companyEmail ? <a href={`mailto:${e.companyEmail}`}>{e.companyEmail}</a> : '-'}</Field>
                                <Field label="Personal Email">{e.personalEmail ? <a href={`mailto:${e.personalEmail}`}>{e.personalEmail}</a> : '-'}</Field>
                                <Field label="Phone">{e.phoneNumber ? <a href={`tel:${e.phoneNumber.replace(/[^0-9+]/g, '')}`}>{e.phoneNumber}</a> : '-'}</Field>
                                <Field label="IbexIQ User">{e.user?.email ?? 'No user account'}</Field>
                            </Section>

                            <Section title="Employment">
                                <Field label="Job Title">{e.jobTitle?.description ?? '-'}</Field>
                                <Field label="Division">{e.department?.parentDepartment ? stripDepartmentPrefix(e.department.parentDepartment.description) : '-'}</Field>
                                <Field label="Department">{e.department ? stripDepartmentPrefix(e.department.description) : '-'}</Field>
                                <Field label="Manager">
                                    {e.manager
                                        ? <a role="button" className="table-link" onClick={() => onClickEmployee(e.manager!.id)}>{fullName(e.manager)}</a>
                                        : '-'}
                                </Field>
                                <Field label="Employee Class">{e.employeeClass?.description ?? '-'}</Field>
                                <Field label="Start Date">{formatDate(e.hireDate as any)}</Field>
                                <Field label="Contract End Date">
                                    {isEditing ? (
                                        <input
                                            type="date"
                                            className="input"
                                            value={contractEndDate}
                                            onChange={(ev) => onChangeContractEndDate(ev.target.value)}
                                        />
                                    ) : (contractEndDate ? formatDate(contractEndDate) : '-')}
                                </Field>
                                <Field label="Terminated">{formatDate(e.terminatedAt as any)}</Field>
                                {e.inactiveReason && <Field label="Inactive Reason">{e.inactiveReason}</Field>}
                                <Field label="Billability Target">{e.billability != null ? `${Number(e.billability)}%` : '-'}</Field>
                                <Field label="Non-professional">{yesNo(e.nonProfessional)}</Field>
                                <Field label="Willing to Relocate">
                                    {isEditing ? (
                                        <select
                                            className="input"
                                            value={willingToRelocate ? "yes" : "no"}
                                            onChange={(ev) => onChangeWillingToRelocate(ev.target.value === "yes")}
                                        >
                                            <option value="yes">Yes</option>
                                            <option value="no">No</option>
                                        </select>
                                    ) : yesNo(willingToRelocate)}
                                </Field>
                                <Field label="ESA Exempt">
                                    {isEditing ? (
                                        <select
                                            className="input"
                                            value={esaExempt ? "yes" : "no"}
                                            onChange={(ev) => onChangeEsaExempt(ev.target.value === "yes")}
                                            title="Exempt from Employment Standards Act overtime and similar rules"
                                        >
                                            <option value="yes">Yes</option>
                                            <option value="no">No</option>
                                        </select>
                                    ) : yesNo(esaExempt)}
                                </Field>
                                {e.headOfDepartments && e.headOfDepartments.length > 0 && (
                                    <Field label="Head of">{e.headOfDepartments.map(d => stripDepartmentPrefix(d.description)).join(', ')}</Field>
                                )}
                            </Section>

                            <Section title="Office">
                                <Field label="Location">{e.location?.description ?? '-'}</Field>
                                <Field label="Address">
                                    {address
                                        ? [address.street1, address.street2, address.city, address.region?.name, address.postalCode, address.country?.name].filter(Boolean).join(', ')
                                        : '-'}
                                </Field>
                            </Section>

                            <Section title="Demographics">
                                <Field label="Gender">{e.gender?.description ?? '-'}</Field>
                                <Field label="Identity">{e.identity?.description ?? '-'}</Field>
                                <Field label="Aboriginal Status">{yesNo(e.aboriginalStatus)}</Field>
                            </Section>
                        </div>

                        <div className="employee-details__grid">
                            <Section title="Designations">
                                {e.designations && e.designations.length > 0 ? (
                                    <table className="table table-sm mb-0">
                                        <thead><tr><th>Designation</th><th>Status</th><th>Start</th><th>End</th></tr></thead>
                                        <tbody>
                                            {e.designations.map((d, i) => (
                                                <tr key={i}>
                                                    <td>{d.designation?.description ?? '-'}</td>
                                                    <td>{d.designationStatus?.description ?? '-'}</td>
                                                    <td>{formatDate(d.startDate as any)}</td>
                                                    <td>{formatDate(d.endDate as any)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : <span className="text-muted">No designations</span>}
                            </Section>

                            <Section title="Emergency Contacts">
                                {e.emergencyContacts && e.emergencyContacts.length > 0 ? (
                                    <table className="table table-sm mb-0">
                                        <thead><tr><th>Name</th><th>Relation</th><th>Phone</th></tr></thead>
                                        <tbody>
                                            {e.emergencyContacts.map(c => (
                                                <tr key={c.id}>
                                                    <td>{c.firstName} {c.lastName}</td>
                                                    <td>{c.relationType?.name ?? '-'}</td>
                                                    <td><a href={`tel:${c.phoneNumber.replace(/[^0-9+]/g, '')}`}>{c.phoneNumber}</a></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : <span className="text-muted">No emergency contacts</span>}
                            </Section>

                            <Section title="Vacation Hours">
                                {e.vacationHours && e.vacationHours.length > 0 ? (
                                    <table className="table table-sm mb-0">
                                        <thead><tr><th>Year</th><th className="text-end">Hours</th></tr></thead>
                                        <tbody>
                                            {e.vacationHours.map(v => (
                                                <tr key={v.year}><td>{v.year}</td><td className="text-end">{Number(v.hours).toFixed(2)}</td></tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : <span className="text-muted">No vacation hours</span>}
                            </Section>
                        </div>

                        <Section title={`Direct Reports (${e.directReports?.length ?? 0})`}>
                            {e.directReports && e.directReports.length > 0 ? (
                                <table className="table table-sm mb-0">
                                    <thead><tr><th>Code</th><th>Name</th><th>Job Title</th></tr></thead>
                                    <tbody>
                                        {e.directReports.map(r => (
                                            <tr key={r.id}>
                                                <td><a role="button" className="table-link" onClick={() => onClickEmployee(r.id)}>{r.employeeCode ?? '-'}</a></td>
                                                <td>{fullName(r)}</td>
                                                <td>{r.jobTitle?.description ?? '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : <span className="text-muted">No direct reports</span>}
                        </Section>

                        <div className="text-muted small">
                            Created {formatDate(e.createdAt as any)} · Last updated {formatDate(e.updatedAt as any)}
                        </div>
                    </>
            }
            {modal}
        </Container>
    </div>
}

export default EmployeeDetailsTemplate
